// src/app/modules/user/modules/accounts/pages/PatientListingPage.tsx

import React, {useEffect, useState, useCallback} from 'react'
import axios from 'axios'
import {useReduxAuth} from '../../../auth'
import {toast} from 'react-toastify'
import {useNavigate, useLocation} from 'react-router-dom'
import PatientForm from '../../components/forms/PatientForm'
import ConfirmationModal from '../../components/modals/ConfirmationModal'
import {RoutesMetaForDoctors} from '../../modules/accounts/components/CRUDWrapper/routesMeta'
import {API_ENDPOINTS} from '../../../../constants/apiConstants'
import {USER_ROLES} from '../../../../constants/StatusConstants'
import MetaTags from '../../../../meta/MetaTags'
import PatientTable, {ActionDefault, Patient} from '../../components/patient/PatientTable'
import Loader from '../../components/Loader'
import moment from 'moment'
import {useMADoctorDropdown} from '../../context/MADoctorDropdownContext'

// Stable noop function to prevent useEffect dependency changes
const noop = () => {}

interface PendingStatusChange {
  id: number
  newStatus: number
  patientName: string
}

export default function PatientListingPage() {
  const {currentUser} = useReduxAuth()
  const roleId = currentUser?.role_id
  const navigate = useNavigate()
  const location = useLocation()
  
  const maDropdown = useMADoctorDropdown()
  const maSelectedDoctorId = roleId === USER_ROLES.MEDICAL_ASSISTANT ? maDropdown.selectedDoctorId : undefined
  const setMaSelectedDoctorId = roleId === USER_ROLES.MEDICAL_ASSISTANT ? maDropdown.setSelectedDoctorId : noop
  const [maDoctors, setMaDoctors] = useState<Array<{id: number; first_name: string; last_name: string; full_name: string}>>([])

  // ── RAW DATA ──
  const [allPatients, setAllPatients] = useState<Patient[]>([])
  const [loading, setLoading] = useState(false)
  const [totalPatientsCount, setTotalPaitentsCount] = useState<number | undefined>()
  const [trialLimit, setTrialLimit] = useState<number | null>(null)
  const [subscriptionStatus, setSubscriptionStatus] = useState<string | null>(null)

  // ── FILTER STATE (mirrored in URL) ──
  const [filters, setFilters] = useState({
    searchTerm: '',
    timeSpent: '',
    lastReading: '',
    readingCount: '',
    status: 'active',
  })

  // ── MODAL / ACTION STATE ──
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [toDelete, setToDelete] = useState<Patient | null>(null)
  const [statusConfirmOpen, setStatusConfirmOpen] = useState(false)
  const [pendingStatus, setPendingStatus] = useState<PendingStatusChange | null>(null)
  const [statusNotes, setStatusNotes] = useState('')
  const [isStatusChangeLoading, setIsStatusChangeLoading] = useState(false)
  const [isPatientCreating, setIsPatientCreating] = useState(false)

  // ── A) On mount, read filters out of URL once ──
  useEffect(() => {
    const qs = new URLSearchParams(location.search)
    setFilters({
      searchTerm: qs.get('searchTerm') || '',
      timeSpent: qs.get('timeSpent') || '',
      lastReading: qs.get('lastReading') || '',
      readingCount: qs.get('readingCount') || '',
      status: qs.get('status') || 'active',
    })
  }, [location.search])

  // Fetch all doctors for MA 
  useEffect(() => {
    if (roleId === USER_ROLES.MEDICAL_ASSISTANT) {
      axios
        .get(API_ENDPOINTS.GET_ALL_DOCTORS())
        .then((resp) => {
          const fetchedDoctors = resp.data.data?.doctors || resp.data.data || []
          setMaDoctors(fetchedDoctors)
          if (fetchedDoctors.length > 0 && !maSelectedDoctorId) {
            setMaSelectedDoctorId(fetchedDoctors[0].id)
          }
        })
        .catch((err) => {
          setMaDoctors([])
        })
    }
  }, [roleId, maSelectedDoctorId, setMaSelectedDoctorId])

  // ── B) Fetch data ──
  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const qsParams = new URLSearchParams({
        status: filters.status, // will be 'active' or 'inactive'
      })
      
      // Add doctor_id for MA
      if (roleId === USER_ROLES.MEDICAL_ASSISTANT && maSelectedDoctorId) {
        qsParams.append('doctor_id', maSelectedDoctorId.toString())
      }
      
      let resp
      if (roleId === USER_ROLES.ADMIN || roleId === USER_ROLES.SUB_ADMIN) {
        resp = await axios.get(API_ENDPOINTS.GET_ALL_PATIENTS_FOR_ADMIN(qsParams.toString()))
      } else {
        resp = await axios.get(API_ENDPOINTS.GET_MY_PATIENTS(qsParams.toString()))
        const {
          trial_limit,
          subscription_status,
          active_patient_count,
        } = resp.data.data

        setTotalPaitentsCount(active_patient_count)
        setTrialLimit(trial_limit)
        setSubscriptionStatus(subscription_status)
      }
      setAllPatients(resp.data.data.patients)
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Error loading patients')
    } finally {
      setLoading(false)
    }
  }, [roleId, filters.status, maSelectedDoctorId]) 

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handleFilterChange = (field: keyof typeof filters, value: string) => {
    // 1) update local
    const next = {...filters, [field]: value}
    setFilters(next)

    // 2) update URL
    const qs = new URLSearchParams(location.search)
    if (field === 'searchTerm') {
      value ? qs.set('searchTerm', value) : qs.delete('searchTerm')
    }
    Object.entries(next).forEach(([key, val]) => {
      if (val) qs.set(key, val)
      else qs.delete(key)
    })

    navigate({pathname: location.pathname, search: qs.toString()}, {replace: true})
  }

  // ── D) Apply filters to raw data ──
  const filtered = allPatients.filter((u) => {
    // Time spent filter
    const mins = Number(u.care_minutes || 0)
    if (filters.searchTerm && !u.full_name.toLowerCase().includes(filters.searchTerm.toLowerCase()))
      return false
    if (
      (filters.timeSpent === '0-9' && !(mins >= 0 && mins < 10)) ||
      (filters.timeSpent === '10-19' && !(mins >= 10 && mins < 20)) ||
      (filters.timeSpent === '20-29' && !(mins >= 20 && mins < 30)) ||
      (filters.timeSpent === '30-39' && !(mins >= 30 && mins < 40)) ||
      (filters.timeSpent === '40-49' && !(mins >= 40 && mins < 50)) ||
      (filters.timeSpent === '50-59' && !(mins >= 50 && mins < 60)) ||
      (filters.timeSpent === '60' && mins < 60)
    )
      return false

    // Last reading filter (hours ago)
    const hrsAgo = u.lastReading?.reading_taken_at
      ? moment().diff(moment(u.lastReading?.reading_taken_at), 'hours', true)
      : null
    if (
      (filters.lastReading === 'never' && hrsAgo != null) ||
      (filters.lastReading === '<24' && (hrsAgo == null || hrsAgo > 24)) ||
      (filters.lastReading === '24-48' && (hrsAgo == null || hrsAgo < 24 || hrsAgo >= 48)) ||
      (filters.lastReading === '48-72' && (hrsAgo == null || hrsAgo < 48 || hrsAgo >= 72)) ||
      (filters.lastReading === '>72' && (hrsAgo == null || hrsAgo <= 72))
    )
      return false

    // MTD reads count
    const cnt = u.readings
    if (
      (filters.readingCount === '0-4' && !(cnt >= 0 && cnt <= 4)) ||
      (filters.readingCount === '5-10' && !(cnt >= 5 && cnt <= 10)) ||
      (filters.readingCount === '11-19' && !(cnt >= 11 && cnt <= 19)) ||
      (filters.readingCount === '20+' && cnt < 20)
    )
      return false

    if ((filters.status === 'active' && !u.status) || (filters.status === 'inactive' && u.status))
      return false

    return true
  })

  // ── E) Handle row actions ──
  const handleRowAction = (a: ActionDefault, p: Patient) => {
    if (a.name === 'VIEW') {
      navigate(`/patient-details/${p.uuid}/health-metrics`)
    } else if (a.name === 'DELETE') {
      setToDelete(p)
      setDeleteConfirmOpen(true)
    } else if (a.name === 'CHANGE_STATUS') {
      setPendingStatus({
        id: p.id,
        newStatus: (p as any).status === 1 ? 0 : 1, // adjust if your Patient type has `status`
        patientName: p.full_name,
      })
      setStatusNotes('')
      setStatusConfirmOpen(true)
    }
  }

  const doDelete = async () => {
    if (!toDelete) return
    try {
      await axios.delete(API_ENDPOINTS.DELETE_PATIENT(toDelete.id))
      toast.success('Deleted!')
      setAllPatients((arr) => arr.filter((x) => x.id !== toDelete.id))
    } catch (err: any) {
      toast.error(err.response?.data?.message)
    } finally {
      setDeleteConfirmOpen(false)
    }
  }

  const doChangeStatus = async () => {
    setIsStatusChangeLoading(true)
    if (!pendingStatus) return

    setStatusConfirmOpen(true)

    try {
      await axios.put(
        API_ENDPOINTS.UPDATE_PATIENT_STATUS(pendingStatus.id, pendingStatus.newStatus),
        {notes: statusNotes}
      )

      toast.success('Patient status updated successfully')

      // Remove the patient from the list
      setAllPatients((prev) => prev.filter((p) => p.id !== pendingStatus.id))

      // Clear state (optional)
      setPendingStatus(null)
      setStatusNotes('')
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to update status')
    } finally {
      setIsStatusChangeLoading(false)
      setStatusConfirmOpen(false)
    }
  }

  const handleCreatePatient = async (values: any, resetForm: any) => {
    setIsPatientCreating(true)
    try {
      const requestBody = {...values}
      if (roleId === USER_ROLES.MEDICAL_ASSISTANT && maSelectedDoctorId) {
        requestBody.doctor_id = maSelectedDoctorId
      }
      
      // If a consent form file was attached, send multipart/form-data so backend can receive file
      if (values && values.consent_form instanceof File) {
        const fd = new FormData()
        // append other fields
        Object.entries(requestBody).forEach(([k, v]) => {
          if (k === 'consent_form') return
          // Append arrays/objects as JSON strings
          if (v && typeof v === 'object' && !(v instanceof File)) {
            fd.append(k, JSON.stringify(v))
          } else if (v !== undefined && v !== null) {
            fd.append(k, String(v))
          }
        })
        fd.append('consent_form', values.consent_form)

        await axios.post(
          API_ENDPOINTS.CREATE_PATIENT(currentUser!.id, roleId!.toString()),
          fd,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        )
      } else {
        await axios.post(API_ENDPOINTS.CREATE_PATIENT(currentUser!.id, roleId!.toString()), requestBody)
      }
      toast.success('Patient created')
      resetForm()
      await fetchData()
    } catch (error: any) {
      toast.error(error?.response?.data?.message)
    } finally {
      setIsPatientCreating(false)
      setIsFormOpen(false)
    }
  }

  let isTrialLimitReached = false
  if (
    subscriptionStatus === 'trial' &&
    trialLimit !== null &&
    totalPatientsCount !== undefined
  ) {
    isTrialLimitReached = totalPatientsCount >= trialLimit
  }

  const handlePatientAddClick = () => {
    if (isTrialLimitReached) {
              toast.warning(
          <div>
            {currentUser?.role_id === 2 ? (
              // Doctor role - show subscription link
              <>
                Subscribe to add more patients. <br />
                <a href="/subscription" style={{color: '#007bff', textDecoration: 'underline'}}>
                  Click here to subscribe
                </a>
              </>
            ) : (
              // Staff role - ask admin to subscribe
              <>
                Patient limit reached. <br />
                Please ask your administrator to upgrade the subscription.
              </>
            )}
          </div>
        )
      return
    }
    setIsFormOpen(true)
  }

  return (
    <>
      <MetaTags title='Patients List' />
      {loading ? (
        <Loader />
      ) : (
        <>
          {/* Doctor dropdown for MA */}
          {roleId === USER_ROLES.MEDICAL_ASSISTANT && (
            <div className='d-flex justify-content-end align-items-center mb-3'>
              <div className='w-25'>
                <label htmlFor='doctorSelect' className='form-label'>
                  Select Doctor
                </label>
                <select
                  id='doctorSelect'
                  className='form-select'
                  value={maSelectedDoctorId ?? ''}
                  onChange={(e) => {
                    const val = e.target.value
                    setMaSelectedDoctorId(val ? Number(val) : undefined)
                  }}
                  disabled={maDoctors.length === 0}
                >
                  {maDoctors.length === 0 ? (
                    <option value=''>-- No doctors available --</option>
                  ) : (
                    <>
                      <option value=''>-- Select Doctor --</option>
                      {maDoctors.map((d) => (
                        <option key={d.id} value={d.id}>
                          {d.full_name || `${d.first_name} ${d.last_name}`}
                        </option>
                      ))}
                    </>
                  )}
                </select>
              </div>
            </div>
          )}
          <PatientTable
            data={{patients: filtered, total: totalPatientsCount, trialLimit}}
            showAddButton={[USER_ROLES.DOCTOR, USER_ROLES.NURSE, USER_ROLES.MEDICAL_ASSISTANT].includes(roleId!)}
            onAddClick={handlePatientAddClick}
            actions={RoutesMetaForDoctors.patient.actions}
            onAction={handleRowAction}
            filterValues={filters}
            onFilterChange={handleFilterChange}
          />

          <ConfirmationModal
            isOpen={deleteConfirmOpen}
            title='Confirm Deletion'
            message='Are you sure you want to delete this patient?'
            confirmButtonTitle='Yes, delete'
            onConfirm={doDelete}
            onCancel={() => setDeleteConfirmOpen(false)}
          />

          <ConfirmationModal
            isLoading={isStatusChangeLoading}
            isDanger={pendingStatus?.newStatus === 1 ? false : true}
            isOpen={statusConfirmOpen}
            title={`${pendingStatus?.newStatus === 1 ? 'Activate' : 'Deactivate'} Patient?`}
            message={
              pendingStatus
                ? `Are you sure want to ${
                    pendingStatus.newStatus === 1 ? 'activate' : 'deactivate'
                  } patient ${
                    pendingStatus.patientName
                  }? For HIPAA compliance, please record the reason for this status change`
                : ''
            }
            requireNotes
            notes={statusNotes}
            onNotesChange={setStatusNotes}
            confirmButtonTitle='Confirm'
            onConfirm={doChangeStatus}
            onCancel={() => setStatusConfirmOpen(false)}
          />

          <PatientForm
            isOpen={isFormOpen}
            onClose={() => setIsFormOpen(false)}
            handleSubmit={handleCreatePatient}
            isLoading={isPatientCreating}
            mode='add'
            // handleSubmit={async (values, resetForm) => {
            //   await axios.post(
            //     API_ENDPOINTS.CREATE_PATIENT(currentUser!.id, roleId!.toString()),
            //     values
            //   )
            //   toast.success('Patient created')
            //   resetForm()
            //   setIsFormOpen(false)
            //   await fetchData()
            // }}
          />
        </>
      )}
    </>
  )
}
